import type {ReactElement} from 'react';
import {useMemo} from 'react';
import type {ColorStorage} from '@Components/color-picker-v2';
import {ColorPicker} from '@Components/color-picker-v2';
import {Swatches} from '@Components/color-picker-v2/components/swatches';
import {Text, TextSize} from '@Components/text';
import {hexToRgba, rgbaToHex} from '@Utils/color.util';
import {onAddBrandAssetClickedHandler} from '@Components/poster-editor/library/poster-editor-library';
import {useStyleProperty} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {getEmailColorsInUse} from '@EmailMakerReact/lib/email-maker-color-usage-library';
import {applyDescriptorColor, getDisplayHex} from '../../controls/color-control/color-control.library';
import {RightPanelOverlayHeader} from '../../components/right-panel-overlay-header/right-panel-overlay-header';
import {COLOR_PICKER_ID} from './color-picker-view.types';
import type {ColorPickerViewProps} from './color-picker-view.types';
import styles from './color-picker-view.module.scss';

export function ColorPickerView({editor, target, onBack, onClose}: ColorPickerViewProps): ReactElement {
  const {component, descriptor} = target;
  const rawValue = useStyleProperty(editor, component, descriptor.property);

  const pickerColor = useMemo<ColorStorage[]>(() => [{id: COLOR_PICKER_ID, color: hexToRgba(getDisplayHex(rawValue, descriptor))}], [rawValue, descriptor]);

  const emailColors = useMemo((): string[] => getEmailColorsInUse(editor), [editor]);

  function writeFromColorStorage(colors: ColorStorage[]): void {
    const first = colors[0];
    if (!first) {
      return;
    }
    applyDescriptorColor(editor, component, descriptor, rgbaToHex(first.color, false));
  }

  function writeFromHex(hex: string): void {
    applyDescriptorColor(editor, component, descriptor, hex);
  }

  return (
    <div className={styles.view}>
      <RightPanelOverlayHeader title={window.i18next.t(descriptor.labelKey)} backAriaLabel={window.i18next.t('pmwjs_back')} onBack={onBack} onClose={onClose} />
      <div className={styles.body}>
        <ColorPicker
          id={COLOR_PICKER_ID}
          color={pickerColor}
          onMyDesign={[]}
          onChangeColor={writeFromColorStorage}
          onChangeEnded={writeFromColorStorage}
          onAddBrandAssetClicked={(): void => {
            void onAddBrandAssetClickedHandler(true);
          }}
          isGradientEnabled={false}
          showOnMyDesignsSection={false}
          isAlphaDisabled
        />
        {renderEmailColors(emailColors, writeFromHex)}
      </div>
    </div>
  );
}

function renderEmailColors(colors: string[], onSelect: (hex: string) => void): ReactElement | null {
  if (colors.length === 0) {
    return null;
  }
  return (
    <div className={styles.emailColors}>
      <Text val={window.i18next.t('pmwjs_on_my_email')} size={TextSize.MD} bold className={styles.heading} />
      <Swatches className="spacing-m-t-2" colors={colors} onClick={onSelect} showAddButton={false} hasCustomMargins />
    </div>
  );
}
